/* ──────────────────────────────────────────────────────────────────────────
   Shared icon system.

   Every interface glyph comes from Tabler Icons. This adapter preserves the
   existing Numero component names and defaults, so every screen gets one
   consistent SVG family without coupling feature code to the vendor package.
   ────────────────────────────────────────────────────────────────────────── */

import {
  IconAlertTriangle,
  IconBookmark,
  IconBookmarkFilled,
  IconCamera,
  IconCompass,
  IconUserCircle,
  IconCircleCheck,
  IconCircleCheckFilled,
  IconHeart,
  IconPlayerPlayFilled,
  IconRosetteDiscountCheckFilled,
  IconTrendingUp,
  IconVolume,
  IconVolumeOff,
  IconArchive,
  IconArrowBackUp,
  IconArrowDown,
  IconArrowUp,
  IconCalendar,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconClock,
  IconCommand,
  IconCopy,
  IconCopyPlus,
  IconDeviceDesktop,
  IconDeviceMobile,
  IconDeviceTablet,
  IconDots,
  IconDownload,
  IconExternalLink,
  IconEye,
  IconFilter,
  IconGripVertical,
  IconHistory,
  IconHome,
  IconInfoCircle,
  IconLayersIntersect,
  IconLink,
  IconPalette,
  IconPencil,
  IconPhoto,
  IconPin,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconSettings,
  IconShieldCheck,
  IconSparkles,
  IconStar,
  IconStarFilled,
  IconTag,
  IconTemplate,
  IconTrash,
  IconUpload,
  IconUserMinus,
  IconUserX,
  IconUsers,
  IconVideoMinus,
  IconWorld,
  IconX,
  type IconProps as TablerIconProps,
  type TablerIcon,
  IconRadar,
  IconBomb,
  IconKey,
  IconLogout,
  IconMoon,
  IconSun,
  IconSend,
  IconCoins,
  IconPlug,
  IconLock,
  IconUsersGroup,
  IconUsersPlus,
  IconFlame,
  IconFileText,
  IconBolt,
  IconRobot,
  IconCurrencyDollar,
  IconUserCheck,
  IconBrandInstagram,
  IconPhoneCall,
  IconArrowsSplit2,
  IconClipboardText,
  IconBrandReddit,
  IconBrandTelegram,
  IconBrandFacebook,
  IconBrandX,
  IconBrandTiktok,
  IconStars,
} from "@tabler/icons-react";

export type IconProps = TablerIconProps;

function Glyph({
  icon: Component,
  size = 15,
  stroke = 1.7,
  ...props
}: IconProps & { icon: TablerIcon }) {
  return <Component size={size} stroke={stroke} aria-hidden="true" focusable="false" {...props} />;
}

const tabler = (icon: TablerIcon) => {
  const NumeroIcon = (props: IconProps) => <Glyph icon={icon} {...props} />;
  NumeroIcon.displayName = `Numero${icon.displayName ?? "Icon"}`;
  return NumeroIcon;
};

export const ChevronIcon = ({
  direction = "down",
  style,
  ...props
}: IconProps & { direction?: "up" | "down" | "left" | "right" }) => {
  const rotate = { down: 0, up: 180, left: 90, right: -90 }[direction];
  return <Glyph icon={IconChevronDown} {...props} style={{ transform: `rotate(${rotate}deg)`, ...style }} />;
};

export const SearchIcon = tabler(IconSearch);
export const UserMinusIcon = tabler(IconUserMinus);
export const UserXIcon = tabler(IconUserX);
export const VideoMinusIcon = tabler(IconVideoMinus);
export const PlusIcon = tabler(IconPlus);
export const CheckIcon = tabler(IconCheck);
export const CloseIcon = tabler(IconX);
export const CopyIcon = tabler(IconCopy);
export const LinkIcon = tabler(IconLink);
export const GlobeIcon = tabler(IconWorld);
export const UsersIcon = tabler(IconUsers);
export const SubFlowIcon = tabler(IconUsersPlus);
export const FireIcon = tabler(IconFlame);
export const TagIcon = tabler(IconTag);
export const LayersIcon = tabler(IconLayersIntersect);
export const ChartIcon = tabler(IconChartBar);
export const ClockIcon = tabler(IconClock);
export const CalendarIcon = tabler(IconCalendar);
export const SettingsIcon = tabler(IconSettings);
export const HistoryIcon = tabler(IconHistory);
export const HomeIcon = tabler(IconHome);
export const TemplateIcon = tabler(IconTemplate);
export const TrashIcon = tabler(IconTrash);
export const EditIcon = tabler(IconPencil);
export const DuplicateIcon = tabler(IconCopyPlus);
export const PauseIcon = tabler(IconPlayerPause);
export const PlayIcon = tabler(IconPlayerPlay);
export const ArchiveIcon = tabler(IconArchive);
export const PinIcon = tabler(IconPin);

export const StarIcon = ({ filled = false, ...props }: IconProps & { filled?: boolean }) => (
  <Glyph icon={filled ? IconStarFilled : IconStar} {...props} />
);

export const AlertIcon = tabler(IconAlertTriangle);
export const InfoIcon = tabler(IconInfoCircle);
export const ShieldIcon = tabler(IconShieldCheck);
export const EyeIcon = tabler(IconEye);
export const ImageIcon = tabler(IconPhoto);
export const UploadIcon = tabler(IconUpload);
export const DownloadIcon = tabler(IconDownload);
export const GripIcon = tabler(IconGripVertical);
export const MoreIcon = tabler(IconDots);
export const FilterIcon = tabler(IconFilter);

export const SortIcon = ({ direction = "desc", ...props }: IconProps & { direction?: "asc" | "desc" }) => (
  <Glyph icon={direction === "asc" ? IconArrowUp : IconArrowDown} {...props} />
);

export const ExternalIcon = tabler(IconExternalLink);
export const RefreshIcon = tabler(IconRefresh);
export const UndoIcon = tabler(IconArrowBackUp);
export const PaletteIcon = tabler(IconPalette);
export const CommandIcon = tabler(IconCommand);
export const PhoneIcon = tabler(IconDeviceMobile);
export const DesktopIcon = tabler(IconDeviceDesktop);
export const TabletIcon = tabler(IconDeviceTablet);
export const SparkIcon = tabler(IconSparkles);

/* Added for Signet, 2026-09-28. */
export const RadarIcon = tabler(IconRadar);
export const BombIcon = tabler(IconBomb);
export const KeyIcon = tabler(IconKey);
export const LogoutIcon = tabler(IconLogout);
export const SunIcon = tabler(IconSun);
export const MoonIcon = tabler(IconMoon);
export const SendIcon = tabler(IconSend);
export const CoinsIcon = tabler(IconCoins);
export const PlugIcon = tabler(IconPlug);
export const LockIcon = tabler(IconLock);
export const TeamIcon = tabler(IconUsersGroup);
export const DocumentIcon = tabler(IconFileText);
export const BoltIcon = tabler(IconBolt);
export const RobotIcon = tabler(IconRobot);
/* Added for the Now page, 2026-09-29. */
export const ArrowUpIcon = tabler(IconArrowUp);
export const ArrowDownIcon = tabler(IconArrowDown);
export const DollarIcon = tabler(IconCurrencyDollar);
export const UserCheckIcon = tabler(IconUserCheck);
/* Platforms, for dashboards named after one. X stands for Twitter too. */
export const InstagramIcon = tabler(IconBrandInstagram);
export const CallIcon = tabler(IconPhoneCall);
export const FlowsIcon = tabler(IconArrowsSplit2);
export const PlanIcon = tabler(IconClipboardText);
export const RedditIcon = tabler(IconBrandReddit);
export const TelegramIcon = tabler(IconBrandTelegram);
export const FacebookIcon = tabler(IconBrandFacebook);
export const XIcon = tabler(IconBrandX);
export const TiktokIcon = tabler(IconBrandTiktok);
export const StarsIcon = tabler(IconStars);

// The creator portal's glyphs (2026-10-13).
export const BookmarkIcon = ({ filled = false, ...props }: IconProps & { filled?: boolean }) => <Glyph icon={filled ? IconBookmarkFilled : IconBookmark} {...props} />;
export const CircleCheckIcon = ({ filled = false, ...props }: IconProps & { filled?: boolean }) => <Glyph icon={filled ? IconCircleCheckFilled : IconCircleCheck} {...props} />;
export const HeartIcon = tabler(IconHeart);
export const PlayFilledIcon = tabler(IconPlayerPlayFilled);
export const VerifiedIcon = tabler(IconRosetteDiscountCheckFilled);
export const VolumeIcon = tabler(IconVolume);
export const VolumeOffIcon = tabler(IconVolumeOff);
export const CameraIcon = tabler(IconCamera);
export const CompassIcon = tabler(IconCompass);
export const UserCircleIcon = tabler(IconUserCircle);
export const TrendingIcon = tabler(IconTrendingUp);
